import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateInviteInput, Invite, Member, TransferOwnershipInput, UpdateWorkspaceInput, WorkspaceDetail,
} from '@wrapforce/shared';
import { workspacePath } from '@wrapforce/shared/api-client';
import { api } from '@/lib/api';

export function useWorkspaceDetail(workspaceId: string | null) {
  return useQuery({
    queryKey: [workspaceId, 'workspace-detail'],
    queryFn: () => api.get<WorkspaceDetail>(`/api/v1/workspaces/${workspaceId}`),
    enabled: !!workspaceId,
    staleTime: 30_000,
  });
}

export function useMembers(workspaceId: string | null) {
  return useQuery({
    queryKey: [workspaceId, 'members'],
    queryFn: () => api.get<Member[]>(workspacePath(workspaceId!, '/members')),
    enabled: !!workspaceId,
    staleTime: 15_000,
  });
}

export function useInvites(workspaceId: string | null) {
  return useQuery({
    queryKey: [workspaceId, 'invites'],
    queryFn: () => api.get<Invite[]>(workspacePath(workspaceId!, '/invites')),
    enabled: !!workspaceId,
    staleTime: 15_000,
  });
}

function useInvalidateWorkspace(workspaceId: string | null) {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: [workspaceId, 'members'] });
    queryClient.invalidateQueries({ queryKey: [workspaceId, 'invites'] });
    queryClient.invalidateQueries({ queryKey: [workspaceId, 'workspace-detail'] });
  };
}

export function useCreateInvite(workspaceId: string | null) {
  const invalidate = useInvalidateWorkspace(workspaceId);
  return useMutation({
    mutationFn: (input: CreateInviteInput) => api.post<Invite>(workspacePath(workspaceId!, '/invites'), input),
    onSuccess: invalidate,
  });
}

export function useRevokeInvite(workspaceId: string | null) {
  const invalidate = useInvalidateWorkspace(workspaceId);
  return useMutation({
    mutationFn: (inviteId: string) => api.delete(workspacePath(workspaceId!, `/invites/${inviteId}`)),
    onSuccess: invalidate,
  });
}

export function useUpdateMemberRole(workspaceId: string | null) {
  const invalidate = useInvalidateWorkspace(workspaceId);
  return useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: string }) =>
      api.patch(workspacePath(workspaceId!, `/members/${userId}`), { role }),
    onSuccess: invalidate,
  });
}

export function useSetMemberActive(workspaceId: string | null) {
  const invalidate = useInvalidateWorkspace(workspaceId);
  return useMutation({
    mutationFn: ({ userId, active }: { userId: string; active: boolean }) =>
      api.post(workspacePath(workspaceId!, `/members/${userId}/${active ? 'reactivate' : 'deactivate'}`)),
    onSuccess: invalidate,
  });
}

export function useRemoveMember(workspaceId: string | null) {
  const invalidate = useInvalidateWorkspace(workspaceId);
  return useMutation({
    mutationFn: (userId: string) => api.delete(workspacePath(workspaceId!, `/members/${userId}`)),
    onSuccess: invalidate,
  });
}

export function useTransferOwnership(workspaceId: string | null) {
  const invalidate = useInvalidateWorkspace(workspaceId);
  return useMutation({
    mutationFn: (input: TransferOwnershipInput) => api.post(workspacePath(workspaceId!, '/ownership-transfer'), input),
    onSuccess: invalidate,
  });
}

export function useDeleteWorkspace(workspaceId: string | null) {
  return useMutation({
    mutationFn: (confirmName: string) => api.delete(workspacePath(workspaceId!, ''), { confirmName }),
  });
}

export function useUpdateWorkspace(workspaceId: string | null) {
  const invalidate = useInvalidateWorkspace(workspaceId);
  return useMutation({
    mutationFn: (input: UpdateWorkspaceInput) => api.patch(workspacePath(workspaceId!, ''), input),
    onSuccess: invalidate,
  });
}
